import type { EventDataMap, PropsEventData, SeriesConfig } from '@/pages/dashboard/components/EventMetricTrend/types';
import { normalizeRecordForEventWindowsDisplay } from '@/pages/dashboard/components/EventMetricTrend/eventRecordDisplayUtils';
import type {
  TAnomalyResultMap,
  IAnomalyDimItem,
  TAnomalyDimSeries,
} from '../AnomalyService';
import type { INormalizedMetricsTrend } from './_type';
import { formatFilterValues, formatSingleTime } from '@/pages/dashboard/utils/utils';
import { getDefaultUtcDateValue } from '@/pages/dashboard/utils/dateUtils';
import { getName } from '@home/utils/common';
import { omit } from 'lodash-es';
import moment from 'moment';

/** KPI 趋势：日粒度下区间（含首尾）≤ minDays 天时请求固定拉满 minDays 天；> minDays 天与筛选一致 */
const KPI_TREND_DEFAULT_MIN_RANGE_DAYS = 14;

export type TKpiTrendApiTimeRange = {
  start_time: string;
  end_time: string;
};

/**
 * 仅 daily：结束日不变，起始日前推，使区间内自然日数（含首尾）为 minDays。
 * 若原区间已大于 minDays 天则不改。
 * @param minDays 默认 14；ROAS 等图表可传 30
 */
export const expandKpiTrendRangeToMinDays = (
  startTime: string,
  endTime: string,
  period: string,
  minDays: number = KPI_TREND_DEFAULT_MIN_RANGE_DAYS,
): TKpiTrendApiTimeRange => {
  const n =
    typeof minDays === 'number' && minDays > 0
      ? Math.floor(minDays)
      : KPI_TREND_DEFAULT_MIN_RANGE_DAYS;
  if (period !== 'daily') {
    return { start_time: startTime, end_time: endTime };
  }
  const s = moment(String(startTime).trim()).startOf('day');
  const e = moment(String(endTime).trim()).startOf('day');
  if (!s.isValid() || !e.isValid() || e.isBefore(s)) {
    return { start_time: startTime, end_time: endTime };
  }
  const inclusiveDays = e.diff(s, 'days') + 1;
  if (inclusiveDays > n) {
    return { start_time: startTime, end_time: endTime };
  }
  const newStart = e.clone().subtract(n - 1, 'days');
  return {
    start_time: newStart.format('YYYY-MM-DD'),
    end_time: e.format('YYYY-MM-DD'),
  };
};

export const formatYmdToIsoDate = (str: string): string => {
  if (!str || str.length !== 8) {
    return str;
  }
  const year = str.slice(0, 4);
  const month = str.slice(4, 6);
  const day = str.slice(6, 8);
  return `${year}-${month}-${day}`;
};

/** 自然日减 N 天（YYYY-MM-DD），用于接口未给 last_week_date 时的 WoW 兜底 */
const isoYmdMinusDays = (isoYmd: string, days: number): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(isoYmd).trim());
  if (!m) {
    return '';
  }
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (Number.isNaN(d.getTime())) {
    return '';
  }
  d.setDate(d.getDate() - days);
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const dayNum = String(d.getDate()).padStart(2, '0');
  return `${y}-${mo}-${dayNum}`;
};

/**
 * 上周版本/同期日期：优先用接口 last_week_date；未下发时用当前行 date 减 7 天（与折线「上周同期」值对齐的日历日）
 */
export const resolveLastWeekDateForTrendRow = (
  currentIsoYmd: string,
  rawFromApi: unknown,
): string => {
  if (rawFromApi !== null && rawFromApi !== undefined && String(rawFromApi).trim() !== '') {
    const s = String(rawFromApi).trim();
    if (s.length === 8 && /^\d{8}$/.test(s)) {
      return formatYmdToIsoDate(s);
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
      return s;
    }
  }
  return isoYmdMinusDays(currentIsoYmd, 7);
};

const normalizeTrendEventList = (raw: unknown): unknown[] => {
  if (Array.isArray(raw)) {
    return raw;
  }
  if (
    raw &&
    typeof raw === 'object' &&
    Array.isArray((raw as { list?: unknown[]; }).list)
  ) {
    return (raw as { list: unknown[]; }).list;
  }
  return [];
};

/** 与 EventMetricTrend 示例一致：eventData / lastEventData 外层 key 为 YYYYMMDD */
const toYyyymmddKey = (input: string | undefined | null): string => {
  if (input === undefined || input === null) {
    return '';
  }
  const s = String(input).trim();
  if (!s) {
    return '';
  }
  if (/^\d{8}$/.test(s)) {
    return s;
  }
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    return `${m[1]}${m[2]}${m[3]}`;
  }
  const digits = s.replace(/\D/g, '');
  return digits.length >= 8 ? digits.slice(0, 8) : '';
};

const splitEventDateRange = (
  eventDate: string | undefined,
): { start: string; end: string; } => {
  if (!eventDate || typeof eventDate !== 'string') {
    return { start: '', end: '' };
  }
  const parts = eventDate.split('~').map(p => p.trim());
  const start = parts[0] || '';
  const end = parts[1] || start;
  return { start, end };
};

/** 事件原始记录：后端返回字段较多且不稳定，按 key → unknown 描述 */
type TEventRecord = Record<string, unknown>;

/** 趋势事件某一天的原始结构（event_list[i].event_record[...] 或 event_list[i] 自身） */
interface ITrendEventDay {
  date?: unknown;
  event_list?: Array<TEventRecord & { event_record?: TEventRecord[]; }>;
  [key: string]: unknown;
}

/** 气泡/图例用：与 Overview EventWindows 共用 name / event_type / description 归一化 */
const normalizeRecordForEventMap = (record: TEventRecord): TEventRecord =>
  normalizeRecordForEventWindowsDisplay(
    record as Record<string, unknown>,
  ) as TEventRecord;

/** 事件下载匹配用：统一为 YYYY-MM-DD，避免 YYYYMMDD 在 Date 解析下失效 */
const normalizeEventYmdField = (v: unknown): string => {
  const s = String(v ?? '').trim();
  if (!s) {
    return '';
  }
  if (/^\d{8}$/.test(s)) {
    return formatYmdToIsoDate(s);
  }
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  if (m) {
    return m[1];
  }
  return s;
};

/** rawEventList 用：与示例一致含 event_title_*、start_time、end_time */
const normalizeRecordForRawList = (record: TEventRecord): TEventRecord => {
  const { start, end } = splitEventDateRange(record?.event_date as string | undefined);
  const rawStart = (record?.start_time as string) || start || '';
  return {
    ...record,
    event_title_en: record?.event_title_en ?? record?.name ?? '',
    event_title_cn: record?.event_title_cn ?? record?.name_cn ?? '',
    start_time: normalizeEventYmdField(rawStart) || String(rawStart).trim(),
    end_time: record?.end_time || end || '',
  };
};

const collectDayEventRecords = (day: ITrendEventDay | undefined | null): TEventRecord[] => {
  const out: TEventRecord[] = [];
  day?.event_list?.forEach((ev) => {
    if (Array.isArray(ev?.event_record)) {
      ev.event_record.forEach((r: TEventRecord) => out.push(r));
    } else if (ev && typeof ev === 'object' && (ev.start_time || ev.event_date)) {
      out.push(ev);
    }
  });
  return out;
};

/**
 * 接口若直接返回「日期 key → 事件数组」（YYYYMMDD 或 YYYY-MM-DD），key 统一为 YYYYMMDD。
 */
const tryLooseGroupedEventMap = (raw: unknown): EventDataMap | null => {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return null;
  }
  const o = raw as Record<string, unknown>;
  if ('list' in o || 'eventData' in o || 'rawEventList' in o) {
    return null;
  }
  const keys = Object.keys(o);
  if (keys.length === 0) {
    return null;
  }
  if (
    !keys.every(
      k => /^\d{8}$/.test(k) || /^\d{4}-\d{2}-\d{2}$/.test(k),
    )
  ) {
    return null;
  }
  const map: EventDataMap = {};
  keys.forEach(k => {
    const v = o[k];
    if (!Array.isArray(v) || v.length === 0) {
      return;
    }
    const ymd = toYyyymmddKey(k);
    if (!ymd) {
      return;
    }
    // normalizeRecordForEventMap 已对 name/name_cn 做兜底（?? ''），运行时符合 EventDataItem 形态；
    // EventDataItem 还要求 event_desc_cn/event_desc_en，上游数据或已带有或运行时被视为 undefined，此处做结构断言保证类型兼容
    map[ymd] = (v as TEventRecord[]).map(r => normalizeRecordForEventMap(r)) as unknown as EventDataMap[string];
  });
  return Object.keys(map).length > 0 ? map : null;
};

const emptyEventDataMap: EventDataMap = {};

const EMPTY_PROPS_EVENT_DATA: PropsEventData = {
  eventData: emptyEventDataMap,
  lastEventData: {},
  rawEventList: [],
};

/**
 * 由趋势事件列表构建与 EventMetricTrend 示例一致的 PropsEventData：
 * - eventData / lastEventData：key 为 YYYYMMDD，值为完整事件对象数组
 * - rawEventList：展平，含 event_title_en/cn、start_time、end_time
 */
export const buildPropsEventDataFromEventList = (raw: unknown): PropsEventData => {
  try {
    if (
      raw &&
      typeof raw === 'object' &&
      !Array.isArray(raw) &&
      (raw as PropsEventData).eventData &&
      typeof (raw as PropsEventData).eventData === 'object'
    ) {
      const p = raw as PropsEventData;
      const ev = p.eventData || {};
      const last = p.lastEventData || {};
      const remap = (m: EventDataMap): EventDataMap => {
        const out: EventDataMap = {};
        Object.keys(m).forEach(k => {
          const ymd = toYyyymmddKey(k);
          if (!ymd) {
            return;
          }
          const arr = m[k];
          if (!Array.isArray(arr) || arr.length === 0) {
            return;
          }
          // 同上：normalizeRecordForEventMap 已做字段兜底，此处断言对齐 EventDataItem
          out[ymd] = arr.map(r => normalizeRecordForEventMap(r)) as unknown as EventDataMap[string];
        });
        return out;
      };
      return {
        eventData: remap(ev as EventDataMap),
        lastEventData: remap(last as EventDataMap),
        rawEventList: Array.isArray(p.rawEventList)
          ? (p.rawEventList as TEventRecord[]).map(normalizeRecordForRawList)
          : [],
      };
    }

    const loose = tryLooseGroupedEventMap(raw);
    if (loose) {
      const rawFlat = Object.values(loose)
        .flat()
        .map(r => normalizeRecordForRawList(r)) as PropsEventData['rawEventList'];
      return {
        eventData: loose,
        lastEventData: {},
        rawEventList: rawFlat,
      };
    }

    const list = normalizeTrendEventList(raw);
    const eventData: Record<string, TEventRecord[]> = {};
    const rawList: TEventRecord[] = [];

    const seenRaw = new Set<string>();

    list.forEach((dayRaw) => {
      const day = dayRaw as ITrendEventDay | null | undefined;
      const ymd = toYyyymmddKey(day?.date as string | undefined);
      if (!ymd) {
        return;
      }
      const records = collectDayEventRecords(day);
      if (records.length === 0) {
        return;
      }
      if (!eventData[ymd]) {
        eventData[ymd] = [];
      }
      records.forEach((record: TEventRecord) => {
        const forMap = normalizeRecordForEventMap(record);
        eventData[ymd].push(forMap);

        const rawItem = normalizeRecordForRawList(record);
        const rawKey =
          rawItem.id !== undefined && rawItem.id !== null && rawItem.id !== ''
            ? String(rawItem.id)
            : `${rawItem.start_time}_${rawItem.end_time}_${rawItem.event_title_en}`;
        if (seenRaw.has(rawKey)) {
          return;
        }
        seenRaw.add(rawKey);
        rawList.push(rawItem);
      });
    });

    return {
      eventData: eventData as EventDataMap,
      lastEventData: {},
      rawEventList: rawList as PropsEventData['rawEventList'],
    };
  } catch {
    return { ...EMPTY_PROPS_EVENT_DATA };
  }
};

/**
 * 折线 data[i] 为 undefined 时，底层 addChartLabel 会生成 { value: undefined, label, itemStyle, ... }，
 * axis tooltip 取 item.data.value / item.value 均为 undefined，会回退到整段 data 对象，dataFormat 原样返回对象后
 * getEchartTooltipString 报 Objects are not valid as a React child。
 * 异动分析 trend_dau 行通常各列有值；metrics_trend 可能缺字段，须按 pageConfig.metrics 显式补 ''。
 */
export const fillRequestedMetricSlots = (
  row: Record<string, unknown>,
  requestedMetrics?: string[],
): Record<string, unknown> => {
  const next = { ...row };
  (requestedMetrics || []).forEach(mk => {
    if (!mk || typeof mk !== 'string') {
      return;
    }
    if (next[mk] === undefined || next[mk] === null) {
      next[mk] = '';
    }
    const lastKey = `${mk}_last_value`;
    if (next[lastKey] === undefined || next[lastKey] === null) {
      next[lastKey] = '';
    }
  });
  return next;
};

/** trendData.seriesData：日期类空串；数值类与异动分析一致保留为 number（固定小数位），避免无意义的纯字符串尺度差异 */
export const shapeTrendSeriesRowLikeSpec = (
  row: Record<string, unknown>,
): Record<string, unknown> => {
  const dateKeys = [
    'last_week_date',
    'last_year_date',
    'last_year_version_date',
  ];
  const next: Record<string, unknown> = { ...row };
  dateKeys.forEach(k => {
    if (next[k] === undefined || next[k] === null) {
      next[k] = '';
    } else {
      next[k] = String(next[k]);
    }
  });
  if (next.is_abnormal === undefined) {
    next.is_abnormal = false;
  }
  Object.keys(next).forEach(k => {
    if (
      k === 'date' ||
      k === 'is_abnormal' ||
      dateKeys.includes(k) ||
      k.endsWith('_date')
    ) {
      return;
    }
    const v = next[k];
    if (typeof v === 'number' && Number.isFinite(v)) {
      next[k] = Number(v.toFixed(6));
    }
  });
  return next;
};

/**
 * 按 anomalyMetricKey 从完整异动数据中提取当前图表关心的子集。
 * 未指定 key 时原样返回（向后兼容）。
 */
const isAnomalyDimLeaf = (v: unknown): v is IAnomalyDimItem => {
  if (!v || typeof v !== 'object') {
    return false;
  }
  const o = v as { date?: unknown; anomaly_explain?: unknown; };
  return typeof o.date === 'string' && typeof o.anomaly_explain === 'string';
};

/** 与趋势行 date 对齐：YYYYMMDD 或 ISO 前缀均可比到同一自然日 */
const canonicalTrendDateKey = (input: string | undefined | null): string => {
  if (input === undefined || input === null) {
    return '';
  }
  const s = String(input).trim();
  if (!s) {
    return '';
  }
  if (/^\d{8}$/.test(s)) {
    return s;
  }
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (m) {
    return `${m[1]}${m[2]}${m[3]}`;
  }
  const digits = s.replace(/\D/g, '');
  return digits.length >= 8 ? digits.slice(0, 8) : '';
};

/** 将维度下「单点或列表」展平为按日条目（新接口为列表） */
export const flattenAnomalyDimSeries = (
  v: TAnomalyDimSeries | undefined,
): IAnomalyDimItem[] => {
  if (v === undefined || v === null) {
    return [];
  }
  if (Array.isArray(v)) {
    return v.filter(isAnomalyDimLeaf);
  }
  return isAnomalyDimLeaf(v) ? [v] : [];
};

export const filterAnomalyByMetricKey = (
  anomaly: TAnomalyResultMap | null | undefined,
  anomalyMetricKey: string | undefined,
): TAnomalyResultMap | null | undefined => {
  if (!anomaly || !anomalyMetricKey) {
    return anomaly;
  }
  const sub = anomaly[anomalyMetricKey];
  if (!sub || typeof sub !== 'object') {
    return undefined;
  }
  return { [anomalyMetricKey]: sub } satisfies TAnomalyResultMap;
};

export const collectAnomalyDates = (
  anomaly: TAnomalyResultMap | null | undefined,
): Set<string> => {
  const dates = new Set<string>();
  const walk = (node: unknown) => {
    if (!node || typeof node !== 'object') {
      return;
    }
    const o = node as {
      date?: string;
      anomaly_explain?: string;
    };
    if (typeof o.date === 'string' && o.anomaly_explain !== undefined) {
      const k = canonicalTrendDateKey(o.date);
      if (k) {
        dates.add(k);
      }
    }
    Object.values(node as object).forEach(walk);
  };
  walk(anomaly);
  return dates;
};

type TSeriesRowWithAbnormal = {
  date?: string;
  is_abnormal?: boolean;
};

export const mergeAnomalyIntoSeriesData = <T extends TSeriesRowWithAbnormal>(
  seriesData: T[] | undefined,
  anomaly: TAnomalyResultMap | null | undefined,
): T[] => {
  if (!Array.isArray(seriesData) || seriesData.length === 0) {
    return seriesData || [];
  }
  try {
    const dateSet = collectAnomalyDates(anomaly);
    if (dateSet.size === 0) {
      return seriesData.map(row => ({ ...row, is_abnormal: row.is_abnormal ?? false }));
    }
    return seriesData.map(row => {
      const d = row.date;
      const abnormal = d ? dateSet.has(canonicalTrendDateKey(String(d))) : false;
      return {
        ...row,
        is_abnormal: abnormal || row.is_abnormal === true,
      };
    });
  } catch {
    return seriesData.map(row => ({ ...row, is_abnormal: row.is_abnormal ?? false }));
  }
};

export const shouldApplyAnomalyMerge = (
  anomaly: TAnomalyResultMap | null | undefined,
  anomalyRequestError: unknown,
): boolean => {
  if (anomalyRequestError) {
    return false;
  }
  return (
    anomaly !== undefined &&
    anomaly !== null &&
    typeof anomaly === 'object' &&
    Object.keys(anomaly as object).length > 0
  );
};

const ANOMALY_SCATTER_COLOR = '#E4675e';

/**
 * 异动接口与 metrics_trend 维度命名常见歧义：
 * - 渠道：接口 `applovin` ↔ trend `applovin_adn`；`moloco` ↔ `moloco_adn`；`unityads` ↔ `unityads_adn`
 * - 国家/分隔符：`apple_search_ads` ↔ `apple search ads`
 *
 * 仅做命名归一化（lower-case、空白→下划线、去掉 `_adn` 后缀），不修改业务数据。
 * 严格匹配优先，归一化匹配作为兜底，避免破坏现有「subKey === dimRawCode」的精确匹配。
 */
const normalizeAnomalySubKey = (raw: string | undefined | null): string => {
  if (typeof raw !== 'string') {
    return '';
  }
  let s = raw.trim().toLowerCase();
  if (!s) {
    return '';
  }
  s = s.replace(/\s+/g, '_');
  s = s.replace(/_adn$/i, '');
  return s;
};

/**
 * 解析「平台/维度原始值 + 指标」拼成的异动子键，如 `1_register_arpu`（1 为 dimRawCode，指标与 anomalyMetricKey 一致）。
 * 使用已归一化的后缀规则，避免指标名内含下划线时被错误截断。
 */
const tryParseCompositeAnomalyPlatformKey = (
  subKey: string,
  anomalyMetricKey: string,
): string | null => {
  const nk = normalizeAnomalySubKey(subKey);
  const mk = normalizeAnomalySubKey(anomalyMetricKey);
  if (!nk || !mk) {
    return null;
  }
  const suffix = `_${mk}`;
  if (!nk.endsWith(suffix)) {
    return null;
  }
  const platform = nk.slice(0, -suffix.length);
  return platform || null;
};

/**
 * KpiEventDimensionTrend 维拆趋势：每平台一条线，真实取值列名为 `dim_v_0`、`dim_v_1`…
 * 异动子键仍为 `{dimRawCode}_{anomalyMetricKey}`，需用 dimRawCode + 占位 metricKey 对齐。
 */
const isDimensionTrendScatterMetricKey = (metricKey: string | undefined): boolean => {
  if (typeof metricKey !== 'string' || metricKey === '') {
    return false;
  }
  return /^dim_v_\d+$/.test(metricKey);
};

/**
 * 分平台等维拆 Tab：折线均为 `dim_v_*`，无总体序列。
 * 此时 `all` 层仅含 `All` 子键的异动表示全局口径，不应绑到某一平台折线上。
 */
const isPureDimensionTrendScatterConfigs = (configs: SeriesConfig[]): boolean =>
  configs.length > 0 &&
  configs.every(c => isDimensionTrendScatterMetricKey(c.metricKey));

/** 趋势在异常日是否有可绑定的 y 值（空串/缺数不画异常点，也不出「检测异常」图例） */
const isPlottableAnomalySourceValue = (srcVal: unknown): boolean => {
  if (srcVal === undefined || srcVal === null) {
    return false;
  }
  if (typeof srcVal === 'number') {
    return Number.isFinite(srcVal);
  }
  if (typeof srcVal === 'string') {
    const t = srcVal.trim();
    if (t === '') {
      return false;
    }
    return !Number.isNaN(Number(t));
  }
  return false;
};

/**
 * 根据 anomaly 数据为每条匹配的折线生成一个独立的散点系列。
 * 多条散点系列共享图例名「异动 / Anomaly」，可一键切换显隐。
 *
 * 匹配规则（优先级递减）：
 *  1. subKey === 'All'  → 第一条 series；若当前图为纯维拆（均为 `dim_v_*`）则跳过（总体异动不画在单平台线上）
 *  2. subKey === seriesConfig[i].anomalySubKey
 *  3. subKey === seriesConfig[i].dimRawCode
 *  4. subKey === seriesConfig[i].metricKey
 *  5. 归一化后与 2–4 任一字段相等（渠道/国家等命名兜底）
 *  6. subKey 为 `{dimRawCode}_{anomalyMetricKey}`（如归一化后 `1_register_arpu`）→ 先 `dimRawCode`+真实 `metricKey`；再 `dimRawCode`+维拆占位 `dim_v_i`（分平台 Tab）；最后无 `dimRawCode` 时回落到首条与 `anomalyMetricKey` 一致的线（汇总图）
 */
export const buildAnomalyScatterSeries = (
  anomaly: TAnomalyResultMap | null | undefined,
  anomalyMetricKey: string | undefined,
  seriesData: Record<string, unknown>[],
  seriesConfigs: SeriesConfig[],
): { anomalySeriesConfigs: SeriesConfig[]; } => {
  if (!anomaly || !anomalyMetricKey || !seriesData?.length || !seriesConfigs?.length) {
    return { anomalySeriesConfigs: [] };
  }
  const innerMap = anomaly[anomalyMetricKey] as
    | Record<string, TAnomalyDimSeries>
    | undefined;
  if (!innerMap || typeof innerMap !== 'object') {
    return { anomalySeriesConfigs: [] };
  }

  const anomalySeriesConfigs: SeriesConfig[] = [];
  let scatterIdx = 0;

  for (const subKey of Object.keys(innerMap)) {
    const dimSeries = innerMap[subKey];
    const dimItems = flattenAnomalyDimSeries(dimSeries);
    if (dimItems.length === 0) {
      continue;
    }
    const anomalyDates = new Set(
      dimItems.map(it => canonicalTrendDateKey(it.date)).filter(Boolean),
    );

    let targetCfg: SeriesConfig | undefined;
    if (subKey === 'All') {
      if (isPureDimensionTrendScatterConfigs(seriesConfigs)) {
        targetCfg = undefined;
      } else {
        const [firstCfg] = seriesConfigs;
        targetCfg = firstCfg;
      }
    } else {
      targetCfg = seriesConfigs.find(
        c =>
          c.anomalySubKey === subKey ||
          c.dimRawCode === subKey ||
          c.metricKey === subKey,
      );
      if (!targetCfg) {
        const normalizedSubKey = normalizeAnomalySubKey(subKey);
        if (normalizedSubKey) {
          targetCfg = seriesConfigs.find(c => {
            const candidates = [c.anomalySubKey, c.dimRawCode, c.metricKey];
            return candidates.some(
              v => normalizeAnomalySubKey(v as string | undefined) === normalizedSubKey,
            );
          });
        }
      }
      if (!targetCfg) {
        const platformNorm = tryParseCompositeAnomalyPlatformKey(
          subKey,
          anomalyMetricKey,
        );
        if (platformNorm !== null) {
          const metricNorm = normalizeAnomalySubKey(anomalyMetricKey);
          targetCfg = seriesConfigs.find(
            c =>
              typeof c.dimRawCode === 'string' &&
              normalizeAnomalySubKey(c.dimRawCode) === platformNorm &&
              normalizeAnomalySubKey(c.metricKey) === metricNorm,
          );
          if (!targetCfg) {
            targetCfg = seriesConfigs.find(
              c =>
                typeof c.dimRawCode === 'string' &&
                normalizeAnomalySubKey(c.dimRawCode) === platformNorm &&
                isDimensionTrendScatterMetricKey(c.metricKey),
            );
          }
          if (!targetCfg) {
            targetCfg = seriesConfigs.find(
              c => normalizeAnomalySubKey(c.metricKey) === metricNorm,
            );
          }
        }
      }
    }
    if (!targetCfg) continue;

    const anomalyMk = `_anomaly_${scatterIdx}`;

    let plottableAnomalyCount = 0;
    for (const row of seriesData) {
      const dateVal = row.date as string | undefined;
      if (dateVal && anomalyDates.has(canonicalTrendDateKey(String(dateVal)))) {
        const srcVal = row[targetCfg.metricKey];
        if (isPlottableAnomalySourceValue(srcVal)) {
          plottableAnomalyCount += 1;
          row[anomalyMk] = {
            value: srcVal,
            // 描边放到数据点级别：避免图例小圆点被 borderWidth 挤压导致看起来更小
            itemStyle: {
              color: ANOMALY_SCATTER_COLOR,
              borderColor: '#fff',
              borderWidth: 1,
            },
            symbol: 'circle',
            symbolSize: 10,
          };
        } else {
          row[anomalyMk] = null;
        }
      } else {
        row[anomalyMk] = null;
      }
    }

    if (plottableAnomalyCount === 0) {
      for (const row of seriesData) {
        delete row[anomalyMk];
      }
      continue;
    }

    scatterIdx += 1;

    const scatterCfg: SeriesConfig = {
      metricKey: anomalyMk,
      cnName: '检测异常',
      enName: 'Anomaly Detected',
      dateKey: 'date',
      hideInTooltip: true,
      color: ANOMALY_SCATTER_COLOR,
      customSeriesConfig: {
        // 用 line + 隐藏线段的方式，让图例渲染成「线段+圆点」的样式（与其它折线图例一致）
        // 同时保持图上只显示异常点
        type: 'line',
        lineStyle: {
          opacity: 0,
        },
        symbolSize: 10,
        symbol: 'circle',
        z: 20,
        itemStyle: {
          color: ANOMALY_SCATTER_COLOR,
        },
      },
      showSymbol: true,
      label: { show: false },
    };
    anomalySeriesConfigs.push(scatterCfg);
  }

  return { anomalySeriesConfigs };
};

type TMetricCell = {
  value?: string;
  last_value?: string;
  last_avg_value?: string;
  last_month_value?: string;
  last_year_value?: string;
};

/** 生成单行上的一个 metric 列及其 last_value 列的片段，避免对 row 做 no-param-reassign 禁止的直接赋值 */
const metricScalarEntry = (
  key: string,
  raw: string | undefined,
): Record<string, unknown> => {
  if (raw === undefined || raw === '') {
    return { [key]: '' };
  }
  const num = Number(raw);
  return { [key]: Number.isNaN(num) ? raw : num };
};

/**
 * metrics_trend：每指标 value 平铺为 row[metricKey]；
 * 上周同期等用接口的 last_value，平铺为 row[`${metricKey}_last_value`]，与 seriesConfig.metricKey 对齐。
 */
const seriesRowFromMetricsItem = (
  item: {
    date: string;
    metrics?: Record<string, TMetricCell>;
    last_week_date?: unknown;
  },
  requestedMetrics?: string[],
): Record<string, unknown> => {
  const rawDate = item.date;
  const dateIso =
    rawDate && rawDate.length === 8 && /^\d{8}$/.test(rawDate)
      ? formatYmdToIsoDate(rawDate)
      : rawDate;

  const row: Record<string, unknown> = {
    date: dateIso,
    last_week_date: resolveLastWeekDateForTrendRow(
      String(dateIso),
      item.last_week_date,
    ),
    last_year_date: '',
    last_year_version_date: '',
    is_abnormal: false,
  };

  const metrics = item.metrics || {};
  Object.keys(metrics).forEach(k => {
    const cell = metrics[k];
    const lastRaw =
      cell?.last_value ??
      (cell as { sdlw_value?: string; })?.sdlw_value;
    Object.assign(row, metricScalarEntry(k, cell?.value));
    Object.assign(row, metricScalarEntry(`${k}_last_value`, lastRaw));
  });
  return fillRequestedMetricSlots(row, requestedMetrics);
};

/**
 * 后端仍返回 trend_dau_data 时，行内往往只有 dau / last_year_* 等旧字段，
 * 没有 ug_spend 等与 seriesConfig.metricKey 对齐的键，导致折线取值为 undefined。
 * 仅当「只请求一个 metric 且该行缺少该字段」时，用 dau 补全，避免多指标时误把 dau 复制到多条线。
 */
const enrichTrendDauRowForSingleRequestedMetric = (
  row: Record<string, unknown>,
  requestedMetrics?: string[],
): Record<string, unknown> => {
  const out: Record<string, unknown> = { ...row };
  if (!requestedMetrics || requestedMetrics.length !== 1) {
    return out;
  }
  const key = requestedMetrics[0];
  if (!key || key === 'dau') {
    return out;
  }
  const missing =
    out[key] === undefined || out[key] === null || out[key] === '';
  const dauVal = out.dau;
  if (!missing || dauVal === undefined || dauVal === null || dauVal === '') {
    return out;
  }
  const num = typeof dauVal === 'number' ? dauVal : Number(dauVal);
  out[key] = Number.isNaN(num) ? dauVal : num;
  return out;
};

/** 后端趋势接口返回结构：结构较灵活，按已知字段描述 */
interface ITrendDauRow {
  date: string;
  last_week_date?: string;
  last_year_version_date?: string;
  last_year_date?: string;
  is_abnormal?: boolean;
  [key: string]: unknown;
}

interface IMetricsTrendRawResponse {
  /** 新接口：按日聚合 */
  trend_dau_data?: ITrendDauRow[];
  /** 兼容 metrics 列表结构 */
  data?: unknown[];
  [key: string]: unknown;
}

export const normalizeMetricsTrendResponse = (
  raw: IMetricsTrendRawResponse | unknown[] | null | undefined,
  requestedMetrics?: string[],
): INormalizedMetricsTrend => {
  const rawObj = (raw && !Array.isArray(raw) ? raw : undefined) as IMetricsTrendRawResponse | undefined;
  const trendDau = rawObj?.trend_dau_data;
  if (Array.isArray(trendDau) && trendDau.length > 0) {
    const seriesData = trendDau.map((item: ITrendDauRow) => {
      const dateIso = formatYmdToIsoDate(item.date);
      return fillRequestedMetricSlots(
        enrichTrendDauRowForSingleRequestedMetric(
          {
            ...item,
            date: dateIso,
            last_week_date: resolveLastWeekDateForTrendRow(
              dateIso,
              item.last_week_date,
            ),
            last_year_version_date: formatYmdToIsoDate(
              item.last_year_version_date || '',
            ),
            last_year_date: formatYmdToIsoDate(item.last_year_date || ''),
            is_abnormal: item.is_abnormal ?? false,
          },
          requestedMetrics,
        ) as Record<string, unknown>,
        requestedMetrics,
      );
    });
    return {
      xAxisData: trendDau.map((item: { date: string; }) =>
        formatYmdToIsoDate(item.date),
      ),
      seriesData: seriesData.map(row =>
        shapeTrendSeriesRowLikeSpec(row as Record<string, unknown>),
      ),
    };
  }

  // 兼容两种结构：顶层数组、{ data: [...] }
  const rows = Array.isArray(raw) ? raw : rawObj?.data;
  if (Array.isArray(rows) && rows[0] && (rows[0] as Record<string, unknown>).metrics !== undefined) {
    const seriesData = rows.map((item: unknown) =>
      // 运行时结构由后端决定；前置 typeof/metrics 判断保证必要字段存在，这里做结构断言以满足参数签名
      seriesRowFromMetricsItem(
        item as { date: string; metrics?: Record<string, TMetricCell>; last_week_date?: unknown; },
        requestedMetrics,
      ),
    );
    return {
      xAxisData: seriesData.map((r: Record<string, unknown>) => r.date as string),
      seriesData: seriesData.map(row =>
        shapeTrendSeriesRowLikeSpec(row as Record<string, unknown>),
      ),
    };
  }

  return { xAxisData: [], seriesData: [] };
};

/** kpiMetricsTrendBeforeRequest 入参：由 Service 组件聚合 pageConfig 与 filterVal 后传入 */
interface IKpiMetricsTrendReqData {
  filterVal?: Record<string, unknown> & {
    rangeTime?: string[];
    timeType?: string;
    diff?: unknown;
    singleDaily?: string;
    start_date?: string;
    end_date?: string;
    period?: string;
  };
  gameCode?: string;
  metrics?: string[];
  /** 日粒度短区间再拉满的天数 */
  minTrendRangeDays?: number;
  [key: string]: unknown;
}

/**
 * 与后端约定一致：game_code、filters、start_time、end_time、period、metrics
 * metrics 由 pageConfig 中 KpiEventMetricTrendService.metrics 传入
 * data.minTrendRangeDays：日粒度短区间再拉满的天数，默认 14；ROAS 等可配 30
 */
export const kpiMetricsTrendBeforeRequest = (
  data: IKpiMetricsTrendReqData,
): Record<string, unknown> | false => {
  const filterVal = data?.filterVal || {};
  const {
    rangeTime,
    timeType,
    diff,
    singleDaily,
    start_date: sd0,
    end_date: ed0,
    period: periodFromFilter,
    ...filters
  } = filterVal;

  let startTime = sd0;
  let endTime = ed0;
  if (singleDaily) {
    startTime = singleDaily;
    endTime = singleDaily;
  }
  if (Array.isArray(rangeTime) && rangeTime.length >= 2) {
    const [rt0, rt1] = rangeTime;
    startTime = rt0;
    endTime = rt1;
  }

  /**
   * 与 dailyKeyMetricsTrendModel / overview 事件等一致：
   * - diff 为真：非继承场景，日粒度默认取「当前筛选区间」对应的补齐窗口（日：end 不变、start 前推 13 天即共 14 天）；
   * - diff 为假：用户选定区间，单日则 formatSingleTime 补齐为完整自然周/月等。
   * singleDaily 为单日点选时不再套用上述窗口，避免强行拉成 14 天。
   */
  const timeTypeForFmt = timeType || 'Day';
  if (!singleDaily && startTime && endTime) {
    const rangeForCalc =
      Array.isArray(rangeTime) && rangeTime.length >= 2
        ? rangeTime
        : [startTime, endTime];
    const defaultTimeRange = getDefaultUtcDateValue(
      timeTypeForFmt,
      rangeForCalc,
    );
    const rangeTimeArr = diff
      ? defaultTimeRange
      : formatSingleTime(rangeForCalc, timeTypeForFmt);
    if (Array.isArray(rangeTimeArr) && rangeTimeArr.length >= 2) {
      const [st, et] = rangeTimeArr;
      startTime = st;
      endTime = et;
    }
  }

  let period: string | undefined = periodFromFilter;
  if (!period && timeType) {
    if (timeType === 'Month') {
      period = 'monthly';
    } else if (timeType === 'Week') {
      period = 'weekly';
    } else {
      period = 'daily';
    }
  }
  if (!period) {
    period = 'daily';
  }

  const metrics = Array.isArray(data?.metrics) ? data.metrics : [];

  if (!startTime || !endTime) {
    return false;
  }

  const minTrendRangeDays =
    typeof data?.minTrendRangeDays === 'number' && data.minTrendRangeDays > 0
      ? Math.floor(data.minTrendRangeDays)
      : KPI_TREND_DEFAULT_MIN_RANGE_DAYS;

  const rangeExpanded = expandKpiTrendRangeToMinDays(
    startTime,
    endTime,
    period,
    minTrendRangeDays,
  );

  return {
    game_code: data.gameCode,
    filters: formatFilterValues(filters),
    start_time: rangeExpanded.start_time,
    end_time: rangeExpanded.end_time,
    period,
    metrics,
  };
};

export const defaultFormatMetricsTrendData = (raw: IMetricsTrendRawResponse | unknown[] | null | undefined) =>
  normalizeMetricsTrendResponse(raw, undefined);

/**
 * 将 pageConfig / 嵌入列中的 nameEn、nameCn 按当前语言解析为最终 name（供 Excel 表头）。
 * 在 EventMetricTrend 生成下载配置时调用，与图表标题共用同一 isEN。
 */
export const resolveKpiMetricTrendDownloadColumns = (
  columns: Array<Record<string, unknown>> | undefined,
  isEN: boolean,
): Array<Record<string, unknown>> | undefined => {
  if (!columns?.length) {
    return columns;
  }
  return columns.map(col => {
    const nameEn = col.nameEn as string | undefined;
    const nameCn = col.nameCn as string | undefined;
    const name = col.name as string | undefined;
    const displayName = getName(isEN, nameEn, nameCn) || name || '';
    return {
      ...omit(col, ['nameEn', 'nameCn']),
      name: displayName,
    };
  });
};

/** 整份 downloadConfig.columns 解析（可选，与 resolveKpiMetricTrendDownloadColumns 二选一使用） */
export const resolveKpiMetricTrendDownloadConfig = <
  T extends { columns?: Array<Record<string, unknown>>; },
>(
  downloadConfig: T | undefined,
  isEN: boolean,
): T | undefined => {
  if (!downloadConfig || !Array.isArray(downloadConfig.columns)) {
    return downloadConfig;
  }
  if (downloadConfig.columns.length === 0) {
    return downloadConfig;
  }
  const resolved: T = {
    ...downloadConfig,
    columns: resolveKpiMetricTrendDownloadColumns(
      downloadConfig.columns,
      isEN,
    ),
  } satisfies T;
  return resolved;
};
